import ActionsButton        from '@/components/actionsButton/ActionsButton';
import Spinner              from '@/components/spinner/Spinner';
import { Tooltip }          from '@/components/ui/tooltip';
import { Button }           from '@/components/ui/button';
import AddressModel         from '@/models/services/fondations/AddressModel';
import EntityModel          from '@/models/services/fondations/EntityModel';
import useEntityAddressesBox from '@/pages/Entity/hooks/useEntityAddressesBox';
import ApiCollection        from '@widesk-core/models/ApiCollection';
import { Box }              from '@chakra-ui/react';
import { Text }             from '@chakra-ui/react';
import React               from 'react';
import { HiPlus }           from 'react-icons/hi';
import { HiLocationMarker } from 'react-icons/hi';
import { observer }         from 'mobx-react';
import { useTranslation }   from 'react-i18next';
import useFetch             from '@widesk-core/hooks/useFetch';

interface IEntityAddressesBoxProps {
	entity:      EntityModel;
	isReadOnly?: boolean;
}

const EntityAddressesBox = observer(({ entity, isReadOnly }: IEntityAddressesBoxProps) => {
	const { t } = useTranslation();

	const [addressCollection] = React.useState(() => new ApiCollection(AddressModel));

	const { isLoading, fetchAsync } = useFetch(async () => {
		await addressCollection
			.setFilter('entity', entity.id as unknown as string)
			.list();
	});

	React.useEffect(() => {
		fetchAsync();
	}, []);

	const onSuccess = React.useCallback(() => {
		fetchAsync();
	}, [fetchAsync]);

	const { modalElement, openAddModal, openEditModal, openDeleteModal } =
		useEntityAddressesBox({ entityUrn: entity.urn, onSuccess });

	return (
		<>
			{modalElement}

			<Box backgroundColor="white" borderRadius="md" boxShadow="md" padding="20px">
				<Box display="flex" alignItems="center" justifyContent="space-between" mb="15px">
					<Text className="poppins-semibold" fontSize="16px">{t('pages.entity.addresses.title')}</Text>
					<Tooltip content={t('pages.entity.addresses.actions.add.tooltip')} showArrow>
						<AddButton onClick={openAddModal} disabled={isReadOnly} />
					</Tooltip>
				</Box>

				{isLoading ? (
					<Box py="20px">
						<Spinner />
					</Box>
				) : addressCollection.length === 0 ? (
					<Text fontSize="14px" color="gray.400">{t('pages.entity.addresses.empty')}</Text>
				) : (
					<Box display="flex" flexDirection="column">
						{addressCollection.map((address, i = 0) => (
							<Box
								key={address.id}
								borderTop={i > 0 ? '1px solid' : undefined}
								borderColor="gray.100"
								display="flex"
								alignItems="center"
								justifyContent="space-between"
								py="12px"
							>
								<Box display="flex" alignItems="flex-start" gap="12px">
									<Box color="gray.400" mt="2px" fontSize="20px" lineHeight="1">
										<HiLocationMarker />
									</Box>
									<Box display="flex" flexDirection="column" gap="4px">
										<Text lineHeight="1" className="poppins-semibold" fontSize="14px">{address.addressLine}</Text>
										{address.addressLineBis && (
											<Text lineHeight="1" fontSize="12px" color="gray.500">{address.addressLineBis}</Text>
										)}
										<Text lineHeight="1" fontSize="12px" color="gray.500">
											{address.zipcode} {address.city}, {address.country}
										</Text>
									</Box>
								</Box>
								<ActionsButton actions={[
									{ label: t('pages.entity.addresses.actions.edit'),   do: () => openEditModal(address), disabled: isReadOnly },
									{ label: t('pages.entity.addresses.actions.delete'), type: 'remove', do: () => openDeleteModal(address), disabled: isReadOnly },
								]} />
							</Box>
						))}
					</Box>
				)}
			</Box>
		</>
	);
});

export default EntityAddressesBox;

const AddButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
	<Button
		size="sm"
		borderRadius="md"
		backgroundColor="black"
		color="white"
		_hover={{ opacity: '0.8' }}
		onClick={onClick}
		padding="0 8px"
		minWidth="32px"
		height="32px"
		disabled={disabled}
	>
		<HiPlus />
	</Button>
);
